import { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Reveal, MaskLines } from "../components/Reveal";
import { ProductCard } from "../components/ProductCard";
import { EnquiryModal } from "../components/EnquiryModal";
import { getProduct, PRODUCTS } from "../data/products";
import { BRAND, buildWhatsAppLink } from "../data/brand";
import { useSEO } from "../lib/useSEO";

export default function ProductDetail() {
  const { id } = useParams();
  const product = getProduct(id);
  const [enquire, setEnquire] = useState(false);
  useSEO(
    product ? `${product.name} Incense Sticks | SK Fragrances` : "Product | SK Fragrances",
    product ? `${product.name} — ${product.descriptor}. ${product.short} Crafted by SK Fragrances, Bangalore since 1980.` : ""
  );

  if (!product) return <Navigate to="/products" replace />;

  const related = PRODUCTS.filter((p) => p.family === product.family && p.id !== product.id).slice(0, 4);
  const fallback = PRODUCTS.filter((p) => p.id !== product.id).slice(0, 4);
  const suggestions = (related.length ? related : fallback);

  const details = [
    { label: "Fragrance Profile", value: product.descriptor },
    { label: "Category", value: product.categoryLabel },
    { label: "Product Type", value: product.type },
    { label: "Pack Size", value: product.packSize },
    { label: "Burning Time", value: product.burnTime },
    { label: "Origin", value: product.origin },
    { label: "Product Code", value: product.code },
  ];

  const waMsg = `Hi ${BRAND.name}, I am interested in your ${product.name} incense sticks (${product.categoryLabel}). Please share more details.`;

  return (
    <div data-testid="product-detail-page" className="bg-ivory">
      <section className="pt-36 md:pt-44 pb-20 md:pb-28">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="mb-8 text-xs tracking-[0.15em] text-charcoal/50">
            <Link to="/" className="hover:text-charcoal">HOME</Link> / <Link to="/products" className="hover:text-charcoal">PRODUCTS</Link> / <span className="text-charcoal">{product.name.toUpperCase()}</span>
          </div>

          <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-start">
            {/* LEFT — image */}
            <motion.div
              initial={{ opacity: 0, scale: 1.03 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="md:sticky md:top-28"
            >
              <div className="relative overflow-hidden bg-white aspect-[4/5]">
                <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                <span className="absolute top-5 left-5 text-[10px] tracking-[0.25em] bg-ivory/85 backdrop-blur px-3 py-1.5">{product.category.toUpperCase()}</span>
              </div>
            </motion.div>

            {/* RIGHT — info */}
            <div>
              <p className="text-sm tracking-[0.3em] text-gold mb-4">{product.categoryLabel.toUpperCase()}</p>
              <MaskLines lines={[product.name]} className="font-serif text-5xl md:text-7xl leading-none text-charcoal" />
              <Reveal delay={0.2}>
                <p className="mt-6 text-charcoal/70 leading-relaxed max-w-lg">{product.long}</p>
              </Reveal>

              <Reveal delay={0.3}>
                <div className="mt-10">
                  <p className="text-xs tracking-[0.25em] text-charcoal/50 mb-3">FRAGRANCE PROFILE</p>
                  <div className="flex flex-wrap gap-3">
                    {product.profile.map((p) => (
                      <span key={p} className="border border-gold/50 text-charcoal text-sm px-4 py-1.5 tracking-wide">{p}</span>
                    ))}
                  </div>
                </div>
              </Reveal>

              <Reveal delay={0.35}>
                <dl className="mt-10 border-t border-charcoal/12">
                  {details.map((d) => (
                    <div key={d.label} className="flex justify-between py-4 border-b border-charcoal/12 text-sm">
                      <dt className="text-charcoal/55 tracking-wide">{d.label}</dt>
                      <dd className="text-charcoal font-medium text-right">{d.value}</dd>
                    </div>
                  ))}
                </dl>
              </Reveal>

              <Reveal delay={0.4}>
                <div className="mt-10 flex flex-wrap gap-4">
                  <button onClick={() => setEnquire(true)} data-testid="enquire-now-btn" className="btn-line bg-charcoal text-ivory px-10 py-4 text-sm tracking-[0.15em] hover:text-charcoal">
                    ENQUIRE NOW →
                  </button>
                  <a href={buildWhatsAppLink(waMsg)} target="_blank" rel="noreferrer" data-testid="whatsapp-btn" className="btn-line border border-charcoal text-charcoal px-10 py-4 text-sm tracking-[0.15em] hover:text-charcoal">
                    WhatsApp Us
                  </a>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* SUGGESTIONS */}
      <section className="py-20 md:py-28 bg-cream">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <p className="text-sm tracking-[0.3em] text-gold mb-4">YOU MAY ALSO LIKE</p>
          <h2 className="font-serif text-4xl md:text-6xl text-charcoal mb-12">More Fragrances</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {suggestions.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </div>
      </section>

      <EnquiryModal open={enquire} onClose={() => setEnquire(false)} product={product} />
    </div>
  );
}
